import React, { useState } from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Language } from '../services/poiService';
import { useTranslation } from 'react-i18next';

const LanguageSelector: React.FC = () => {
  const { selectedLanguage, languages, setSelectedLanguage, isLoading } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const handleLanguageSelect = (language: Language | null) => {
    setSelectedLanguage(language);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  if (isLoading) {
    return (
      <div className="flex items-center text-gray-500 dark:text-gray-400 dark:text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
        <span className="hidden sm:inline text-sm">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
        aria-label={t('language.selectLanguage')}
      >
        <Globe className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">
          {selectedLanguage ? selectedLanguage.native_name : t('header.language')}
        </span>
        <span className="sm:hidden">
          {selectedLanguage ? selectedLanguage.code.toUpperCase() : 'EN'}
        </span>
        <svg
          className={`ml-1 h-3 w-3 transform transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
            {t('language.selectLanguage')}
          </div>
          {languages.map((language) => (
            <button
              key={language.id}
              onClick={() => handleLanguageSelect(language)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:bg-gray-700 transition-colors ${
                selectedLanguage?.id === language.id
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{language.native_name}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 ml-2">
                  {language.code.toUpperCase()}
                </span>
              </div>
              {language.name !== language.native_name && (
                <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
                  {language.name}
                </div>
              )}
            </button>
          ))}
          {selectedLanguage && (
            <>
              <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
              <button
                onClick={() => handleLanguageSelect(null)}
                className="w-full text-left px-4 py-2 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:bg-gray-700 transition-colors"
              >
                <div className="flex items-center">
                  <span>{t('language.autoDetect')}</span>
                </div>
              </button>
            </>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSelector;